import { Button, Group, Stack } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown, TextSerif } from "@/components/elements";

import type { Car } from "@/types/car";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type CarsTableFiltersPayload = {
  condition?: Car["condition"];
  status?: Car["status"];
};

export type CarsTableFiltersProps = {
  condition?: Car["condition"];
  conditions?: Car["condition"][];
  onCancel?: () => void;
  onSubmit: ({ condition, status }: CarsTableFiltersPayload) => void;
  status?: Car["status"];
  statuses?: Car["status"][];
} & MantineStyleProps;

export const CarsTableFilters: FC<CarsTableFiltersProps> = ({
  condition,
  conditions = [],
  onCancel,
  onSubmit,
  status,
  statuses = [],
  ...props
}) => {
  const { t } = useTranslation();
  // Condition
  const conditionData = conditions.map((condition) => ({
    value: condition,
    label: t(`car.conditions.${condition}`, condition),
  }));
  const [conditionValue, setConditionValue] = useState(condition);
  // Status
  const statusData = statuses.map((status) => ({
    value: status,
    label: t(`car.statuses.${status}`, status),
  }));
  const [statusValue, setStatusValue] = useState(status);

  useEffect(() => {
    setConditionValue(condition);
  }, [condition]);
  useEffect(() => {
    setStatusValue(status);
  }, [status]);

  return (
    <Stack p="lg" {...props}>
      <Group align="center" justify="space-between">
        <TextSerif size="lg">{t("components.CarsTableFilters.title")}</TextSerif>
        <Group justify="flex-end">
          <Dropdown
            data={statusData}
            placeholder={t("car.status")}
            value={statusValue}
            optionProps={{
              style: {
                minWidth: "max-content",
                whiteSpace: "nowrap",
              },
            }}
            onChange={(value) => setStatusValue((value as Car["status"]) || undefined)}
          />
          <Dropdown
            data={conditionData}
            placeholder={t("car.condition")}
            value={conditionValue}
            optionProps={{
              style: {
                minWidth: "max-content",
                whiteSpace: "nowrap",
              },
            }}
            onChange={(value) => setConditionValue((value as Car["condition"]) || undefined)}
          />
        </Group>
      </Group>
      <Group justify="flex-end">
        {onCancel && (
          <Button
            c="red"
            variant="transparent"
            onClick={onCancel}
            aria-label={t("components.CarsTableFilters.cancel")}
          >
            {t("components.CarsTableFilters.cancel")}
          </Button>
        )}
        <Button
          onClick={() => onSubmit({ condition: conditionValue, status: statusValue })}
          aria-label={t("components.CarsTableFilters.submit")}
        >
          {t("components.CarsTableFilters.submit")}
        </Button>
      </Group>
    </Stack>
  );
};
